import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight } from 'lucide-react';

const LoginPage = () => {
  const { login, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  
  // Prüfe auf Redirect-Reason von Route Guards
  useEffect(() => {
    const reason = sessionStorage.getItem('auth_redirect_reason');
    if (reason) {
      setLoginError(reason);
      toast.error(reason);
      sessionStorage.removeItem('auth_redirect_reason');
      
      if (process.env.NODE_ENV === 'development') {
        console.warn('[LoginPage] Redirect reason:', reason);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[LoginPage] Login response:', {
          email: response.data.user?.email,
          role: response.data.user?.role,
          hasToken: !!response.data.access_token
        });
      }
      
      login(response.data.access_token, response.data.user);
      
      toast.success('Erfolgreich angemeldet!');
      navigate('/');
    } catch (error) {
      let errorMessage = 'Anmeldung fehlgeschlagen. Bitte prüfen Sie E-Mail und Passwort.';
      
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;
        
        if (detail) {
          errorMessage = detail;
        } else if (status === 401) {
          errorMessage = 'Ungültige E-Mail oder Passwort.';
        } else if (status === 403) {
          errorMessage = 'Ihr Konto ist deaktiviert. Bitte kontaktieren Sie den Administrator.';
        } else if (status === 500) {
          errorMessage = 'Serverfehler. Bitte versuchen Sie es später erneut.';
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.error('[LoginPage] Login error:', { status, detail, errorMessage });
        }
      } else if (error.request) {
        errorMessage = 'Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung.';
      }
      
      setLoginError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="login-page">
      {/* Hauptbereich */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
        <div className="w-full max-w-[360px] sm:max-w-[400px]">
          
          {/* Logo & Branding */}
          <div className="text-center mb-8 sm:mb-10">
            <img 
              src="/vivi_logo.png" 
              alt="VIVI Logo" 
              className="h-16 sm:h-20 w-auto mx-auto mb-6"
              data-testid="login-logo"
            />
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-slate-800 mb-2" 
              data-testid="login-headline"
            >
              Willkommen
            </h1>
            <p className="text-slate-500 text-sm sm:text-base mb-1">
              Vending Inventory Visualization Interface
            </p>
          </div>

          {/* Fehleranzeige */}
          {loginError && (
            <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-lg" data-testid="login-error">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-sm text-red-600">{loginError}</p>
              </div>
            </div>
          )}

          {/* Login-Formular */}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Input
                id="email"
                type="email"
                placeholder="E-Mail"
                value={formData.email}
                onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setLoginError(null); }}
                required
                className="h-12 bg-white border-slate-200 focus:border-slate-400 focus:ring-slate-400/20 text-base placeholder:text-slate-400"
                data-testid="email-input"
              />
            </div>

            <div>
              <Input
                id="password"
                type="password"
                placeholder="Passwort"
                value={formData.password}
                onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setLoginError(null); }}
                required
                className="h-12 bg-white border-slate-200 focus:border-slate-400 focus:ring-slate-400/20 text-base placeholder:text-slate-400"
                data-testid="password-input"
              />
            </div>

            {/* Login-Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 group"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span>Anmelden...</span>
              ) : (
                <>
                  <span>Anmelden</span>
                  <ArrowRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>
          </form>

          {/* Passwort vergessen */}
          <div className="mt-5 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="forgot-password-link"
            >
              Passwort vergessen?
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 sm:py-8 text-center" data-testid="login-footer">
        <div className="space-y-2">
          <p className="text-xs text-slate-400" data-testid="app-version">
            Version 1.6
          </p>
          <div className="text-xs text-slate-300">
            <span>Ein Projekt von </span>
            <a 
              href="https://www.lolliolli.de" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-slate-400 transition-colors"
              data-testid="developer-link"
            >
              LolliOlli
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
